import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';
import { withRouter } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Formik, FormikHelpers, Form } from 'formik';

import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import { Typography } from '@material-ui/core';
import FormTextField from 'components/FormFields/FormTextField';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import EditIcon from '@material-ui/icons/EditOutlined';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import ArrowForwardIos from '@material-ui/icons/ArrowForwardIos';

import DialogHeader from 'components/DialogHeader/DialogHeader';
import MaterialTable from 'components/Table/Table';
import FileUpload from 'components/FormFields/FileUpload';
//import Tooltip from 'components/Tooltip/Tooltip';
import Background from 'components/Layout/Background';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import { RootStore } from 'stores/rootStore';
import { BackofficeInstitutions } from 'stores/admin/backofficeInstitutions';
import { FinancialInstitution } from 'pages/Accounts/Edit/components/ExternalProducts/model';

interface Props {
  rootStore: RootStore;
}

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

const initialInst = {
  id: '',
  internal: false,
  title: '',
  logo: '',
  icon: '',
  colour: '',
};

const FinInstitutions: React.FC<Props> = ({ rootStore }) => {
  const { t } = useTranslation();
  const [store] = useState(new BackofficeInstitutions(rootStore));
  const { fetchInstitutions, editOpen } = store;
  const [uploadFile, setUploadFile] = useState<{
    logo?: { name: string; data: string };
    icon?: { name: string; data: string };
  }>({});
  const [deleteId, setDeleteId] = useState<string | undefined>(undefined);

  useEffect(() => {
    fetchInstitutions();
  }, [fetchInstitutions, store]);

  const onFileChange = (e: any, type: 'logo' | 'icon') => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = (readerEvt: any) => {
      let binaryString = readerEvt.target.result;
      setUploadFile({ ...uploadFile, [type]: { name: file.name, data: btoa(binaryString) } });
    };
    reader.readAsBinaryString(file);
  };

  const submit = async (values: FinancialInstitution, formikHelpers: FormikHelpers<FinancialInstitution>) => {
    const ok = await store.handleSubmit(
      { ...values, logo: uploadFile.logo?.data || store.data?.logo, icon: uploadFile.icon?.data || store.data?.icon },
      formikHelpers,
    );
    if (ok) setUploadFile({});
  };

  return (
    <Container maxWidth="md">
      <Background inside>
        <Typography variant="h1">{t('backOffice_finInstitutions_title')}</Typography>

        {store.institutions && (
          <MaterialTable
            options={{
              header: true,
              sorting: false,
              draggable: false,
              search: false,
              toolbar: false,
              emptyRowsWhenPaging: false,
              paging: false,
            }}
            quickfix
            data={store.institutions}
            columns={[
              {
                title: t('general_name'),
                field: 'title',
                defaultSort: 'asc',
              },
              {
                title: '',
                //field: 'date',
                render: ({ id, internal }) => (
                  <>
                    <IconButton
                      size="medium"
                      onClick={async () => {
                        store.fetchData(id);
                        store.setEditOpen(true);
                      }}
                    >
                      <EditIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton size="medium" onClick={() => setDeleteId(id)} disabled={internal}>
                      <DeleteIcon fontSize="inherit" />
                    </IconButton>
                  </>
                ),
              },
            ]}
          />
        )}

        <div style={{ display: 'flex', justifyContent: 'center', padding: '16px' }}>
          <Button
            onClick={() => {
              store.setEditOpen(true);
            }}
            variant="contained"
            color="primary"
          >
            {t(`backOffice_finInstitutions_new`)}
          </Button>
        </div>
      </Background>

      <ConfirmDialog
        noHeader
        isOpen={deleteId !== undefined}
        confirm={async () => {
          deleteId && (await store.deleteInstitution(deleteId));
          setDeleteId(undefined);
        }}
        close={() => setDeleteId(undefined)}
      >
        {t('backOffice_finInstitutions_confirmDelete')}
      </ConfirmDialog>

      <Formik initialValues={store.data || initialInst} onSubmit={submit} enableReinitialize>
        {(formikProps) => (
          <Dialog open={editOpen} maxWidth="md">
            <DialogHeader
              onClose={() => {
                store.setEditOpen(false);
                store.clearData();
                setUploadFile({});
              }}
            >
              {t('backOffice_finInstitution_settings')}
            </DialogHeader>
            <DialogContent>
              <FormWithoutEnterSubmit id="admin_institutions">
                <Grid container>
                  <Grid item xs={6}>
                    <FormTextField name="title" label={t(`backOffice_finInstitution_title`)} fast={false} />
                  </Grid>
                  <Grid item xs={6} />
                  <Grid item xs={3}>
                    <FormTextField name="colour" label={t(`backOffice_finInstitution_color`)} fast={false} />
                  </Grid>
                  <Grid item xs={3}>
                    <div style={{ width: '100%', height: '38px', backgroundColor: formikProps.values.colour }} />
                  </Grid>
                  <Grid item xs={6} />

                  <Grid container item style={{ alignItems: 'center' }}>
                    <Grid item xs={4}>
                      {t('backOffice_finInstitution_logo')}
                    </Grid>
                    <Grid item xs={3}>
                      <FileUpload name={'logoFile'} accept=".png" onChange={(e: any) => onFileChange(e, 'logo')} />
                    </Grid>
                    <Grid item xs={4}>
                      {uploadFile.logo?.name ? (
                        <>
                          <img
                            src={`data:image/png;base64,${uploadFile.logo?.data}`}
                            alt={uploadFile.logo.name}
                            title={uploadFile.logo.name}
                            height="36"
                          />
                          <br />
                        </>
                      ) : store.data?.logo ? (
                        <img src={`data:image/png;base64,${store.data.logo}`} alt="" height="36" />
                      ) : (
                        ''
                      )}
                    </Grid>
                  </Grid>

                  <Grid container item style={{ alignItems: 'center' }}>
                    <Grid item xs={4}>
                      {t('backOffice_finInstitution_icon')}
                    </Grid>
                    <Grid item xs={3}>
                      <FileUpload name={'iconFile'} accept=".png" onChange={(e: any) => onFileChange(e, 'icon')} />
                    </Grid>
                    <Grid item xs={4}>
                      <div
                        style={{
                          width: '37px',
                          height: '37px',
                          background: formikProps.values.colour,
                          borderRadius: '50%',
                          overflow: 'hidden',
                          border: `solid ${formikProps.values.colour} 2px`
                        }}
                      >
                        {uploadFile.icon?.name && formikProps.values.colour ? (
                          <>
                            <img
                              src={`data:image/png;base64,${uploadFile.icon?.data}`}
                              alt={uploadFile.icon.name}
                              title={uploadFile.icon.name}
                              height="36"
                            />
                            <br />
                          </>
                        ) : store.data?.icon ? (
                          <img
                            src={`data:image/png;base64,${store.data.icon}`}
                            alt=""
                            height="100%"
                            width="100%"
                          />
                        ) : (
                          ''
                        )}
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
              </FormWithoutEnterSubmit>
            </DialogContent>
            <DialogActions>
              <Button
                variant="contained"
                onClick={() => {
                  store.setEditOpen(false);
                  store.clearData();
                  setUploadFile({});
                }}
              >
                {t('general_cancel')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                form="admin_institutions"
                endIcon={<ArrowForwardIos />}
              >
                {t('general_save')}
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Formik>
    </Container>
  );
};

export default compose(inject('rootStore'), withRouter, observer)(FinInstitutions);
